import React, { useState } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input, Label, Textarea } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { ArrowRight, Pencil, Plus, Trash2, MoveLeft, MoveRight, X } from "lucide-react";

function StageDetail({ stage, attributes }) {
  if (!stage) return null;
  const attrByKey = Object.fromEntries((attributes || []).map((a) => [a.key, a]));
  const linked = (stage.attributes || []).map((k) => attrByKey[k]).filter(Boolean);
  const Section = ({ label, items }) => !!items?.length && (
    <div>
      <div className="label-eyebrow mb-1">{label}</div>
      <div className="flex flex-wrap gap-1.5">{items.map((x, i) => <Badge key={i} variant="outline" className="text-[10px]">{x}</Badge>)}</div>
    </div>
  );
  return (
    <div className="card-flat p-4 space-y-3" data-testid="vp-stage-detail">
      <div>
        <div className="font-display font-semibold">{stage.name}</div>
        <p className="text-xs text-muted-foreground mt-0.5">{stage.description || "—"}</p>
        <div className="text-[11px] font-mono mt-1">Data owner: <span className="text-accent">{stage.data_owner || "Unassigned"}</span>
          {stage.source_template && <span className="text-muted-foreground ml-2">· source: {stage.source_template}</span>}</div>
      </div>
      <Section label="Linked attributes" items={linked.map((a) => `${a.name}${a.unit ? ` (${a.unit})` : ""}`)} />
      <Section label="ESG metrics" items={stage.metrics} />
      <Section label="Risks" items={stage.risks} />
      <Section label="Suppliers" items={stage.suppliers} />
      <Section label="Financing opportunities" items={stage.financing} />
    </div>
  );
}

export default function ValueChainMap({ stages, attributes, canEdit, onSave }) {
  const [selected, setSelected] = useState(0);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reason, setReason] = useState("");

  const startEdit = () => { setDraft(stages.map((s) => ({ ...s }))); setEditing(true); };
  const move = (i, dir) => setDraft((d) => {
    const arr = [...d]; const j = i + dir;
    if (j < 0 || j >= arr.length) return arr;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return arr;
  });
  const save = async () => {
    await onSave(draft, reason);
    setEditing(false); setReasonOpen(false); setReason("");
  };

  const list = editing ? draft : stages;
  return (
    <div className="space-y-4" data-testid="vp-value-chain">
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">Value chain — {list.length} stages</div>
        {canEdit && !editing && <Button size="sm" variant="outline" onClick={startEdit} data-testid="vp-chain-edit-btn"><Pencil className="h-3 w-3" /> Edit map</Button>}
        {editing && (
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} data-testid="vp-chain-cancel-btn"><X className="h-3 w-3" /> Cancel</Button>
            <Button size="sm" variant="outline" onClick={() => setDraft((d) => [...d, { key: `stage-${Date.now()}`, name: "New stage", description: "", data_owner: "", attributes: [], metrics: [], risks: [], suppliers: [], financing: [], source_template: "custom" }])} data-testid="vp-chain-add-stage-btn"><Plus className="h-3 w-3" /> Add stage</Button>
            <Button size="sm" variant="primary" onClick={() => setReasonOpen(true)} data-testid="vp-chain-save-btn">Save changes</Button>
          </div>
        )}
      </div>

      <div className="overflow-x-auto scrollbar-thin pb-2">
        <div className="flex items-stretch gap-0 min-w-max">
          {list.map((s, i) => (
            <React.Fragment key={s.key || i}>
              <div
                className={`w-44 shrink-0 border rounded-sm p-3 transition-colors ${!editing && selected === i ? "border-accent bg-accent/5" : "border-border bg-surface hover:border-accent/50"} ${editing ? "" : "cursor-pointer"}`}
                onClick={() => !editing && setSelected(i)} data-testid={`vp-stage-node-${i}`}>
                <div className="text-[10px] font-mono text-muted-foreground mb-1">STAGE {i + 1}</div>
                {editing ? (
                  <Input value={s.name} onChange={(e) => setDraft((d) => d.map((x, xi) => xi === i ? { ...x, name: e.target.value } : x))} className="h-7 text-xs mb-1" data-testid={`vp-stage-name-input-${i}`} />
                ) : (
                  <div className="text-xs font-semibold leading-snug">{s.name}</div>
                )}
                <div className="text-[10px] text-muted-foreground mt-1 truncate">{s.data_owner || "Unassigned"}</div>
                {!editing && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {!!s.attributes?.length && <span className="text-[9px] font-mono px-1 py-0.5 border border-border rounded-sm">{s.attributes.length} attr</span>}
                    {!!s.risks?.length && <span className="text-[9px] font-mono px-1 py-0.5 border border-border rounded-sm text-[#FFB800]">{s.risks.length} risk</span>}
                    {!!s.financing?.length && <span className="text-[9px] font-mono px-1 py-0.5 border border-border rounded-sm text-[#00E65B]">{s.financing.length} fin</span>}
                  </div>
                )}
                {editing && (
                  <div className="flex gap-1 mt-2">
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => move(i, -1)} data-testid={`vp-stage-left-${i}`}><MoveLeft className="h-3 w-3" /></Button>
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => move(i, 1)} data-testid={`vp-stage-right-${i}`}><MoveRight className="h-3 w-3" /></Button>
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setDraft((d) => d.filter((_, xi) => xi !== i))} data-testid={`vp-stage-remove-${i}`}><Trash2 className="h-3 w-3 text-red-400" /></Button>
                  </div>
                )}
              </div>
              {i < list.length - 1 && <div className="flex items-center px-1"><ArrowRight className="h-4 w-4 text-muted-foreground" /></div>}
            </React.Fragment>
          ))}
        </div>
      </div>

      {!editing && <StageDetail stage={list[selected]} attributes={attributes} />}

      <Dialog open={reasonOpen} onOpenChange={setReasonOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reason for the change</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Every value-chain customisation is recorded in the Template Override Log for auditability.</p>
          <div>
            <Label>Reason *</Label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Added a dedicated composting stage to match our operations" data-testid="vp-chain-reason-input" />
          </div>
          <Button variant="primary" onClick={save} disabled={reason.trim().length < 5} data-testid="vp-chain-confirm-save-btn">Save &amp; log override</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
